<template>
  <div class="relative z-0">
    <div id="map" class="w-full h-96 md:h-[calc(100vh-200px)]"></div>
    <div class="bg-white dark:bg-gray-800 p-4 rounded shadow mt-4">
      <!-- Przełącznik tras -->
      <div class="mb-4 flex items-center">
        <label class="mr-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ showDriverRoute ? 'Trasa rzeczywista' : 'Trasa szacowana' }}
        </label>
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            v-model="showDriverRoute"
            class="sr-only peer"
            @change="toggleRoute"
          >
          <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-teal-600"></div>
        </label>
      </div>
      <div v-if="filteredSegments.length > 0" class="segment-details max-w-full sm:max-w-md lg:max-w-lg">
        <h3 class="text-lg font-semibold mb-2">Szczegóły trasy</h3>
        <ul class="space-y-2">
          <li v-for="(segment, index) in filteredSegments" :key="index" class="border-b pb-2 bg-gray-100 dark:bg-gray-800 p-4 rounded">
            <p><strong>Segment {{ index + 1 }}:</strong></p>
            <p>Z: {{ segment.from }}</p>
            <p>Do: {{ segment.to }}</p>
            <p>
              Odległość:
              {{ segment.distance }} km
            </p>
          </li>
        </ul>

        <ul class="space-y-2 mt-4">
          <li v-for="(segment, index) in passengerSegments" :key="index" class="border-b pb-2 bg-gray-200 dark:bg-gray-900 p-4 rounded">
            <p><strong>Pasażer {{ index + 1 }}: {{ segment.firstName }} {{ segment.lastName }}</strong></p>
            <p>Wysadzony w: {{ segment.address }}</p>
          </li>
        </ul>
        <p class="mt-4 font-semibold">
          Całkowita odległość:
          
          <span v-if="showDriverRoute && !props.order?.direction && !props.order?.return">
            {{ (realDistanceRed + accessKilometers + returnKilometers).toFixed(2) }} km
            (Rzeczywista - Czerwona, Z dojazdem i powrotem)
          </span>
          
          <span v-else-if="showDriverRoute && !props.order?.direction && props.order?.return">
            {{ (realDistanceRed + accessKilometers).toFixed(2) }} km
            (Rzeczywista - Czerwona, Z dojazdem, bez powrotu)
          </span>
          
          <span v-else-if="showDriverRoute && props.order?.direction && !props.order?.return">
            {{ (realDistanceRed + returnKilometers).toFixed(2) }} km
            (Rzeczywista - Czerwona, Bez dojazdu, z powrotem)
          </span>
          
          <span v-else-if="showDriverRoute">
            {{ realDistanceRed.toFixed(2) }} km
            (Rzeczywista - Czerwona, Tylko przejazd)
          </span>
          
          <span v-else>
            {{ totalDistance.toFixed(2) }} km
            (Szacowana - Niebieska)
          </span>
        </p>
      </div>
      <div v-else class="text-gray-500">
        Brak szczegółów trasy do wyświetlenia.
      </div>
      <!-- <div v-if="isLoading" class="loading-message my-2">
        Trasa jest w trakcie ładowania, proszę czekać<span class="dots"><span>.</span><span>.</span><span>.</span></span>
      </div> -->
      <div v-if="errorMessage" class="text-red-600 my-2 md:max-w-sm">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, defineProps } from 'vue';
import * as L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import driverLocationService from '@/services/driverLocationService';
import orderService from '@/services/orderService';

// Ustaw ikonę dla punktów odbioru i dostawy (domyślna Leaflet)
const pointIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Ustaw ikonę dla lokalizacji kierowcy (Flaticon CDN)
const driverIcon = L.icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/14703/14703768.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Prop: order
const props = defineProps({
  order: {
    type: Object,
    required: true,
    validator: (order) => {
      return (
        order &&
        order.id &&
        Array.isArray(order.passenger) &&
        order.passenger.length > 0 &&
        order.passenger.every(p => p.pickupAddress && p.dropoffAddress)
      );
    },
  },
  totalWaitingTime: {
    type: Number,
    required: false,
    default: 0,
  },
});

// Computed: orderId i odległość z order
const orderId = computed(() => props.order?.id || null);

// Zmienne reaktywne
const map = ref(null);
const currentPositionMarker = ref(null);
const driverLocationRouteLayer = ref(null); // Warstwa dla trasy kierowcy (rzeczywistej)
const driverRouteLayer = ref(null); // Warstwa dla trasy OSRM
const errorMessage = ref('');
let errorTimeout = null;
const fetchIntervalId = ref(null);
const isMounted = ref(true);
const isLoading = ref(false);
const geocodeCache = new Map();
const routeCache = new Map();
const segmentDetails = ref([]);
const segmentDetailsPassenger = ref([]);
const passengerSegments = ref([]);
const totalDistance = ref(0);
const totalReplayDistance = ref(0);
const showDriverRoute = ref(true); // Stan przełącznika (true: trasa kierowcy, false: trasa OSRM)
const realDistance = ref(0); // Rzeczywisty dystans kierowcy
let realDistanceByMap = ref(0); // Rzeczywisty dystans kierowcy liczony po mapie
let realDist = ref(0);
const realDistanceRed = ref(0);

const accessKilometers = ref(0);
const returnKilometers = ref(0);

const filteredSegments = computed(() => {
  if (showDriverRoute.value) {
    return segmentDetailsPassenger.value;
  }
  return segmentDetails.value.filter(s => !s.additional);
});

// Funkcja obliczania odległości między punktami (Haversine)
function haversineDistance([lat1, lon1], [lat2, lon2]) {
  const R = 6371e3; // Promień Ziemi w metrach
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (R * c) / 1000; // Odległość w km
}

// Funkcja pobierania trasy z OSRM
async function getRoute(start, end) {
  const cacheKey = `${start[0].toFixed(6)},${start[1].toFixed(6)};${end[0].toFixed(6)},${end[1].toFixed(6)}`;
  if (routeCache.has(cacheKey)) {
    return routeCache.get(cacheKey);
  }
  const url = `https://router.project-osrm.org/route/v1/driving/${start[0].toFixed(6)},${start[1].toFixed(6)};${end[0].toFixed(6)},${end[1].toFixed(6)}?overview=full&geometries=geojson`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  try {
    const res = await fetch(url, {
      signal: controller.signal,
      headers: { 'User-Agent': 'MyApp/1.0 (contact@example.com)' },
    });
    clearTimeout(timeout);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(`Nie udało się pobrać trasy: ${data.message || res.statusText}`);
    }
    const data = await res.json();
    if (!data.routes || !data.routes[0].geometry || !Array.isArray(data.routes[0].geometry.coordinates)) {
      throw new Error('Nieprawidłowa odpowiedź OSRM');
    }
    const coords = data.routes[0].geometry.coordinates;
    const distance = data.routes[0].distance / 1000;
    const result = { coords: coords.map(([lng, lat]) => [lat, lng]), distance };
    routeCache.set(cacheKey, result);
    if (routeCache.size > 100) routeCache.clear();
    return result;
  } catch (error) {
    clearTimeout(timeout);
    throw error;
  }
}

async function updateOrderKilometers() {
  if (!props.order || !props.order.id) {
    console.warn("Brak zamówienia lub ID zamówienia, nie można zaktualizować dystansów.");
    return;
  }

  try {
    const orderData = {
      id: props.order.id,
      distanceInKm: realDistance.value || 0,
      driverKilometers: totalReplayDistance.value || 0,
      accessKilometers: accessKilometers.value || 0,
      returnKilometers: returnKilometers.value || 0,
      totalWaitingTime: props.totalWaitingTime || 0,
    };

    // console.log("📦 Aktualizowane dystanse zamówienia:", orderData);

    await orderService.updateKilometers(props.order.id, orderData);

    // console.log("✅ Zaktualizowano dystanse w zamówieniu.");
  } catch (e) {
    console.error("❌ Błąd aktualizacji dystansów zamówienia:", e);
  }
}


function setError(msg) {
  if (errorMessage.value === msg) return;
  errorMessage.value = msg;
  if (errorTimeout) clearTimeout(errorTimeout);
  errorTimeout = setTimeout(() => {
    errorMessage.value = '';
    errorTimeout = null;
  }, 5000);
}

// Funkcja próbkowania punktów
function samplePoints(points, maxPoints = 50) {
  if (points.length <= maxPoints) return points;
  const step = Math.floor(points.length / maxPoints);
  const sampled = [];
  for (let i = 0; i < points.length; i += step) {
    sampled.push(points[i]);
  }
  if (sampled[sampled.length - 1] !== points[points.length - 1]) {
    sampled.push(points[points.length - 1]);
  }
  return sampled;
}

// Funkcja przełączania tras
function toggleRoute() {
  if (!map.value) return;
  if (showDriverRoute.value) {
    // Pokaż trasę kierowcy, ukryj trasę OSRM
    if (driverRouteLayer.value) {
      map.value.removeLayer(driverRouteLayer.value);
    }
    if (driverLocationRouteLayer.value && !map.value.hasLayer(driverLocationRouteLayer.value)) {
      map.value.addLayer(driverLocationRouteLayer.value);
      map.value.fitBounds(driverLocationRouteLayer.value.getBounds());
    }
  } else {
    // Pokaż trasę OSRM, ukryj trasę kierowcy
    if (driverLocationRouteLayer.value) {
      map.value.removeLayer(driverLocationRouteLayer.value);
    }
  }
}

function findClosestLocation(locations, targetTime) {
  return locations.reduce((closest, loc) => {
    const locTime = new Date(loc.createdAt).getTime();
    const diff = Math.abs(locTime - targetTime);
    const closestDiff = Math.abs(new Date(closest.createdAt).getTime() - targetTime);
    return diff < closestDiff ? loc : closest;
  }, locations[0]);
}

async function logFirstAndClosestLocation(locations) {
  if (!locations || locations.length === 0) {
    console.warn("Brak lokalizacji do analizy.");
    return;
  }

  // 1. Lokalizacja najbliższa startDateTime
  const startTime = new Date(props.order.startDateTime).getTime();
  let firstLocation = findClosestLocation(locations, startTime);

  // console.log("🚗 Najbliższa lokalizacja startu:", firstLocation);

  // 2. Lokalizacja najbliższa waitingEndTime (pierwszy pasażer)
  const waitingEnd = new Date(props.order.passenger[0].waitingEndTime).getTime();
  let closestLocation = findClosestLocation(locations, waitingEnd);

  // console.log("🧍 Najbliższa lokalizacja pierwszego pasażera:", closestLocation);

  // 3. OSRM: start kursu → odbiór pierwszego pasażera
  try {
    const route1 = await getRoute(
      [firstLocation.longitude, firstLocation.latitude],
      [closestLocation.longitude, closestLocation.latitude]
    );

    // console.log(route1);

    accessKilometers.value = route1.distance;

    const fromAddress = await reverseGeocode(firstLocation.latitude, firstLocation.longitude);

    // console.log("🏠 Adres startu:", fromAddress);

    const toAddress = await reverseGeocode(closestLocation.latitude, closestLocation.longitude);

    // console.log("🧍 Adres pierwszego pasażera:", toAddress);


    if(!props.order?.direction) {
      segmentDetailsPassenger.value.unshift({
        from: fromAddress,
        to: toAddress,
        distance: route1.distance.toFixed(2),
      });
    }
  } catch (error) {
    console.error("Błąd pobierania trasy OSRM (start → passenger):", error);
  }

  // 4. Lokalizacja najbliższa droppedOffTime ostatniego pasażera
  const droppedOffTime = new Date(props.order.passenger.at(-1).droppedOffTime).getTime();
  let targetLocation = findClosestLocation(locations, droppedOffTime);

  // console.log("🏠 Najbliższa lokalizacja ostatniego pasażera:", targetLocation);

  // 5. OSRM: ostatni pasażer → dom kierowcy
  try {
    const driverAddress = `${props.order.driver.homeAddress}, ${props.order.driver.city}`;
    const driverCoords = await geocodeNominatim(driverAddress);

    const route2 = await getRoute(
      [targetLocation.longitude, targetLocation.latitude],
      driverCoords
    );

    // console.log(route2);

    returnKilometers.value = route2.distance;

    const fromAddress = await reverseGeocode(targetLocation.latitude, targetLocation.longitude);

    // console.log("🏠 Adres ostatniego pasażera:", fromAddress);

    if(!props.order?.return) {
      segmentDetailsPassenger.value.push({
        from: fromAddress,
        to: driverAddress,
        distance: route2.distance.toFixed(2),
      });
    }
  } catch (error) {
    console.error("Błąd pobierania trasy (last passenger → home):", error);
  }
}

async function logPassengersDropoffLocations(locations) {
  if (!locations || locations.length === 0) {
    console.warn("Brak lokalizacji GPS do analizy.");
    return;
  }

  for (const passenger of props.order.passenger) {
    if (!passenger.droppedOffTime) {
      console.warn(`🚫 Pasażer ${passenger.firstName} ${passenger.lastName} nie ma ustawionego droppedOffTime`);
      continue;
    }

    const pickupTime = new Date(passenger.waitingEndTime).getTime();
    const pickupLocation = findClosestLocation(locations, pickupTime);

    const pickupAddress = await reverseGeocode(pickupLocation.latitude, pickupLocation.longitude);

    // console.log(`🧍 Adress Pickup ${passenger.firstName} ${passenger.lastName} ${pickupAddress}`);

    const dropoffTime = new Date(passenger.droppedOffTime).getTime();
    const dropoffLocation = findClosestLocation(locations, dropoffTime);

    const address = await reverseGeocode(dropoffLocation.latitude, dropoffLocation.longitude);

    // console.log(dropoffLocation.latitude, dropoffLocation.longitude);
    // console.log(`🧍 Adress dropoffAddress ${passenger.firstName} ${passenger.lastName} ${address}`);

    passengerSegments.value.push({
      firstName: passenger.firstName,
      lastName: passenger.lastName,
      address: address,
    });

    const route3 = await getRoute(
        [pickupLocation.longitude, pickupLocation.latitude],
        [dropoffLocation.longitude, dropoffLocation.latitude]
      );

    // console.log("TRASA: " + route3.distance)
    realDist.value += route3.distance;

    segmentDetailsPassenger.value.push({
      from: pickupAddress,
      to: address,
      distance: route3.distance.toFixed(2),
    });
  }
}

// Funkcja pobierania trasy kierowcy
async function loadDriverRoute() {
  if (!isMounted.value || !map.value || !orderId.value) {
    setError('Mapa lub zamówienie niedostępne.');
    return;
  }

  let localReplayDistance = 0;

  try {
    isLoading.value = true;
    const response = await driverLocationService.getRouteForOrder(orderId.value);
    const locations = response.data;

    // console.log('Pobrane lokalizacje kierowcy:', locations);
    await logFirstAndClosestLocation(locations);
    await logPassengersDropoffLocations(locations);
    if (!locations || locations.length === 0) {
      if (!errorMessage.value) setError('Brak danych lokalizacji dla trasy kierowcy.');
      isLoading.value = false;
      return;
    }

    const routePoints = locations
      .map(loc => [loc.latitude, loc.longitude])
      .filter(([lat, lng]) => typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng));

    // TEST2
    // if (routePoints.length < 2) {
    //   if (!errorMessage.value) setError('Za mało ważnych punktów trasy kierowcy.');
    //   isLoading.value = false;
    //   return;
    // }

    // Rysowanie trasy kierowcy (rzeczywistej)
    if (driverLocationRouteLayer.value) {
      map.value.removeLayer(driverLocationRouteLayer.value);
    }
    driverLocationRouteLayer.value = L.polyline(routePoints, { color: 'red', weight: 2 })
      .addTo(map.value)
      .bindPopup('Rzeczywista trasa kierowcy');

    // Próbkowanie punktów (max 50 punktów)
    const sampledPoints = samplePoints(routePoints, 50);

    // Filtrowanie bliskich punktów (min. odległość 0.10 km)
    const filteredPoints = [sampledPoints[0]];
    for (let i = 1; i < sampledPoints.length; i++) {
      const prevPoint = filteredPoints[filteredPoints.length - 1];
      const currPoint = sampledPoints[i];
      if (haversineDistance(prevPoint, currPoint) >= 0.10) {
        filteredPoints.push(currPoint);
      }
    }

    // Oblicz rzeczywisty dystans kierowcy (sumowanie po wszystkich punktach)
    // let realDist = 0;
    // for (let i = 1; i < routePoints.length; i++) {
    //   realDist += haversineDistance(routePoints[i - 1], routePoints[i]);
    // }
    // console.log("TRASA2" + realDist.value)
    realDistanceRed.value = realDist.value;
    realDistance.value = realDistanceByMap.value;
    updateOrderKilometers();
    

    // Jeśli chcesz tylko pierwszy i ostatni punkt:
    // let firstLastDistance = 0;
    // if (routePoints.length >= 2) {
    //   firstLastDistance = haversineDistance(routePoints[0], routePoints[routePoints.length - 1]);
    // }

    // Możesz wyświetlić oba dystanse:
    // console.log('Dystans rzeczywisty (cała trasa):', realDistance.value.toFixed(2), 'km');
    // console.log('Dystans pierwszy-ostatni punkt:', firstLastDistance.toFixed(2), 'km');

    // Pobierz trasy równolegle z limitem
    const routeLatLngs = [];
    // ... (pozostawione zakomentowane, jak w oryginale)

    totalReplayDistance.value = localReplayDistance;

    // console.log(`OSRM: Całkowita długość zoptymalizowanej trasy kierowcy: ${totalReplayDistance.value.toFixed(2)} km`);

    // Rysowanie trasy OSRM
    if (driverRouteLayer.value) {
      map.value.removeLayer(driverRouteLayer.value);
    }
    driverRouteLayer.value = L.polyline(routeLatLngs, { color: 'red', weight: 2 })
      .bindPopup(`Zoptymalizowana trasa kierowcy: ${totalReplayDistance.value.toFixed(2)} km`);

    // Domyślnie pokazuj trasę kierowcy
    if (showDriverRoute.value) {
      map.value.addLayer(driverLocationRouteLayer.value);
      map.value.fitBounds(driverLocationRouteLayer.value.getBounds());
    } else {
      map.value.addLayer(driverRouteLayer.value);
      map.value.fitBounds(driverRouteLayer.value.getBounds());
    }

    // Dodaj marker dla aktualnej pozycji kierowcy
    const latestLocation = locations[locations.length - 1];
    const { latitude, longitude } = latestLocation;
    if (currentPositionMarker.value) {
      currentPositionMarker.value.setLatLng([latitude, longitude]);
    } else {
      currentPositionMarker.value = L.marker([latitude, longitude], { icon: driverIcon })
        .addTo(map.value)
        .bindPopup('Aktualna pozycja kierowcy');
    }

    errorMessage.value = '';
    // isLoading.value = false;
  } catch (error) {
    console.error('Błąd ładowania trasy kierowcy:', error);
    if (!errorMessage.value) setError('Błąd przy ładowaniu trasy kierowcy.');
    // isLoading.value = false;
  }
}

// Funkcja pobierania lokalizacji kierowcy
async function fetchDriverLocation() {
  if (!isMounted.value || !map.value || !orderId.value || props.order.orderStatus === 'ZAKONCZONY' || props.order.orderStatus === 'W_TRAKCIE') return;

  try {
    const response = await driverLocationService.getRouteForOrder(orderId.value);
    const locations = response.data;

    if (!locations || locations.length === 0) {
      if (!errorMessage.value) setError('Brak danych lokalizacji dla podanego zamówienia.');
      return;
    }

    const latestLocation = locations[locations.length - 1];
    const { latitude, longitude } = latestLocation;

    if (currentPositionMarker.value) {
      currentPositionMarker.value.setLatLng([latitude, longitude]);
    } else {
      currentPositionMarker.value = L.marker([latitude, longitude], { icon: driverIcon })
        .addTo(map.value)
        .bindPopup('Aktualna pozycja kierowcy');
    }

    // Aktualizuj trasę kierowcy
    const routePoints = locations
      .map(loc => [loc.latitude, loc.longitude])
      .filter(([lat, lng]) => typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng));

    if (routePoints.length >= 2) {
      if (driverLocationRouteLayer.value) {
        map.value.removeLayer(driverLocationRouteLayer.value);
      }
      driverLocationRouteLayer.value = L.polyline(routePoints, { color: 'red', weight: 2})
        .bindPopup('Rzeczywista trasa kierowcy');
      if (showDriverRoute.value) {
        map.value.addLayer(driverLocationRouteLayer.value);
        map.value.fitBounds(driverLocationRouteLayer.value.getBounds());
      }
    }

    errorMessage.value = '';
  } catch (error) {
    console.error('Błąd pobierania lokalizacji:', error);
    if (!errorMessage.value) setError('Błąd przy pobieraniu lokalizacji.');
  }
}

// Funkcja geokodowania
async function geocodeNominatim(address) {
  if (!address || address === 'Brak adresu odbioru' || address === 'Brak adresu dostawy') {
    throw new Error('Nieprawidłowy adres');
  }
  if (geocodeCache.has(address)) {
    return geocodeCache.get(address);
  }
  const normalizedAddress = address.replace(/,\s*/g, ' ').trim();
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(normalizedAddress)}`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  try {
    const res = await fetch(url, {
      headers: { 'User-Agent': 'MyApp/1.0 (contact@example.com)' },
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!res.ok) {
      throw new Error(`Błąd geokodowania: ${res.statusText}`);
    }
    const data = await res.json();
    if (!data.length) {
      errorMessage.value = `Nie znaleziono adresu (Prosze sprawdzić czy dany adres jest poprawny.): ${address}`;
      throw new Error(`Nie znaleziono adresu: ${address}`);
    }
    const validResults = data.filter(result => result.class !== 'waterway' && result.type !== 'river');
    if (!validResults.length) {
      throw new Error(`Nie znaleziono ważnego adresu (wykluczono rzeki): ${address}`);
    }
    // Log filtered results for debugging
    // console.log(`Geocode results for "${address}" after excluding rivers:`, validResults);
    const [lon, lat] = [parseFloat(validResults[0].lon), parseFloat(validResults[0].lat)];
    if (isNaN(lon) || isNaN(lat) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
      throw new Error(`Nieprawidłowe współrzędne dla adresu: ${address}`);
    }

    const coords = [lon, lat];
    geocodeCache.set(address, coords);
    if (geocodeCache.size > 100) geocodeCache.clear();
    return coords;
  } catch (error) {
    clearTimeout(timeout);
    throw error;
  }
}

onMounted(async () => {
  showDriverRoute.value = true;
  map.value = L.map('map').setView([51.6, 18.95], 10);
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | Driver icon by <a href="https://www.flaticon.com">Flaticon</a>',
  }).addTo(map.value);

  try {
    if (!props.order.passenger || props.order.passenger.length === 0) {
      if (!errorMessage.value) setError('Brak pasażerów do wyświetlenia trasy.');
      return;
    }

    // isLoading.value = true;
    const pickupAddresses = props.order.passenger
      .map(p => p.pickupAddress)
      .filter((v, i, a) => a.indexOf(v) === i); // Filtr unikalności
    
    // 2. Budowanie listy punktów dowozu (Dropoffy) z uwzględnieniem sharedDropoff
    const dropoffAddresses = [];
    const seenSharedDropoffs = new Set();
    
    for (const passenger of props.order.passenger) {
      const dropoff = passenger.dropoffAddress;
      const shared = passenger.sharedDropoff; // Zakładamy, że to pole jest dostępne w props.order.passenger
      
      if (Boolean(shared) && dropoff) {
        if (seenSharedDropoffs.add(dropoff)) {
          dropoffAddresses.push(dropoff); // Dodaj raz, jeśli shared=true
        }
      } else if (dropoff) {
        dropoffAddresses.push(dropoff); // Zawsze dodaj, jeśli shared jest false/null
      }
    }
    
    // 3. Budowanie pełnej sekwencji trasy (Pickupy -> Dropoffy)
    const allAddresses = [...pickupAddresses, ...dropoffAddresses];
    
    // 4. Geokodowanie i budowanie listy punktów Leaflet
    const points = [];
    for (const address of allAddresses) {
      try {
        const coords = await geocodeNominatim(address);
        points.push({
          coords: coords,
          address: address,
          type: pickupAddresses.includes(address) ? 'pickup' : 'dropoff',
        });
      } catch (e) {
        console.error(`Błąd geokodowania adresu ${address}:`, e);
        // Kontynuujemy, ale z błędem - można pominąć ten punkt
      }
    }
    // const points = [];
    // for (const passenger of props.order.passenger) {
    //   const pickupCoords = await geocodeNominatim(passenger.pickupAddress);
    //   const dropoffCoords = await geocodeNominatim(passenger.dropoffAddress);
    //   points.push({
    //     coords: pickupCoords,
    //     address: passenger.pickupAddress,
    //     type: 'pickup',
    //   });
    //   points.push({
    //     coords: dropoffCoords,
    //     address: passenger.dropoffAddress,
    //     type: 'dropoff',
    //   });
    // }

    totalDistance.value = 0;
    const routeLatLngs = [];
    segmentDetails.value = [];

    for (let i = 0; i < points.length - 1; i++) {
      const start = points[i].coords;
      const end = points[i + 1].coords;
      try {
        const { coords, distance } = await getRoute(start, end);
        totalDistance.value += distance;
        routeLatLngs.push(...coords);
        segmentDetails.value.push({
          from: points[i].address,
          to: points[i + 1].address,
          distance: distance.toFixed(2),
        });
        realDistanceByMap.value = totalDistance.value;
        // console.log(
        // `OSRM: Segment ${points[i].address} -> ${points[i + 1].address}: ${distance.toFixed(2)} km`
        // );
      } catch (error) {
        console.error(`Błąd obliczania segmentu ${points[i].address} -> ${points[i + 1].address}:`, error);
        if (!errorMessage.value) setError(`Błąd obliczania trasy dla segmentu: ${points[i].address} -> ${points[i + 1].address}`);
        isLoading.value = false;
        return;
      }
    }

    if (!isMounted.value) {
      isLoading.value = false;
      return;
    }

    // console.log('Szczegóły trasy:', segmentDetails.value);
    // console.log(`Całkowita odległość: ${totalDistance.value.toFixed(2)} km`);
    // if (Math.abs(totalDistance.value - orderDistanceKm.value) > 1) {
    //   console.warn(`Rozbieżność w odległości: API=${totalDistance.value.toFixed(2)} km, order.distanceInKm=${orderDistanceKm.value} km`);
    // }

    const polyline = L.polyline(routeLatLngs, { color: 'blue', weight: 4 })
      .addTo(map.value)
      .bindPopup(`Najkrótsza trasa: ${totalDistance.value.toFixed(2)} km`);

    points.forEach(point => {
      L.marker([point.coords[1], point.coords[0]], { icon: pointIcon })
        .addTo(map.value)
        .bindPopup(`${point.type === 'pickup' ? 'Odbiór' : 'Zrzut'}: ${point.address}`);
    });

    map.value.fitBounds(polyline.getBounds());

    // Wstępne ładowanie trasy kierowcy
    await loadDriverRoute();

    // Uruchom śledzenie lokalizacji kierowcy
    await fetchDriverLocation();
    if (props.order.orderStatus == 'W_TRAKCIE') {
      fetchIntervalId.value = setInterval(fetchDriverLocation, 15000);
    }
  } catch (e) {
    console.error('Błąd:', e);
    // errorMessage.value = `Błąd: ${e.message}`;
    isLoading.value = false;
  }

  // if (
  //   (props.order.distanceInKm === null || props.order.distanceInKm === 0) &&
  //   (props.order.driverKilometers === null || props.order.driverKilometers === 0) &&
  //   (props.totalWaitingTime === null || props.totalWaitingTime === 0)
  // )
  // {
    // try {
    //   const orderData = {
    //     id: props.order.id,
    //     distanceInKm: realDistance.value,
    //     driverKilometers: totalReplayDistance.value,
    //     accessKilometers: accessKilometers.value,
    //     returnKilometers: returnKilometers.value,
    //     totalWaitingTime: props.totalWaitingTime,
    //   };

    //   await orderService.updateKilometers(props.order.id, orderData);
    //   console.log('Zaktualizowano dystanse w zamówieniu.');
    // } catch (e) {
    //   console.error('Błąd aktualizacji dystansów zamówienia:', e);
    // }
  // }
});

onUnmounted(() => {
  isMounted.value = false;

  if (fetchIntervalId.value) {
    clearInterval(fetchIntervalId.value);
    fetchIntervalId.value = null;
  }

  if (currentPositionMarker.value && map.value) {
    try {
      map.value.removeLayer(currentPositionMarker.value);
    } catch (e) {
      console.error('Błąd usuwania markera:', e);
    }
    currentPositionMarker.value = null;
  }

  if (driverLocationRouteLayer.value && map.value) {
    try {
      map.value.removeLayer(driverLocationRouteLayer.value);
    } catch (e) {
      console.error('Błąd usuwania warstwy trasy kierowcy:', e);
    }
    driverLocationRouteLayer.value = null;
  }

  if (driverRouteLayer.value && map.value) {
    try {
      map.value.removeLayer(driverRouteLayer.value);
    } catch (e) {
      console.error('Błąd usuwania warstwy trasy OSRM:', e);
    }
    driverRouteLayer.value = null;
  }

  if (map.value) {
    try {
      map.value.off();
      map.value.remove();
    } catch (e) {
      console.error('Błąd usuwania mapy:', e);
    }
    map.value = null;
  }
});

async function reverseGeocode(lat, lon) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom18`;
  try {
    const res = await fetch(url, {
      headers: { 'User-Agent': 'MyApp/1.0 (contact@example.com)' },
    });
    if (!res.ok) {
      throw new Error(`Błąd reverse geocoding: ${res.statusText}`);
    }
    const data = await res.json();
    console.log(data);
    return (
      (data.address.road ? data.address.road + " " : "") +
      (data.address.house_number ? data.address.house_number + " " : "") +
      (data.address.village || data.address.city || data.address.town || "") +
      (data.address.postcode ? " " + data.address.postcode : "") ||
      `(${lat}, ${lon})`
    );
  } catch (e) {
    console.error("❌ Błąd reverse geocoding:", e);
    return `(${lat}, ${lon})`;
  }
}
</script>

<style scoped>
.segment-details ul {
  list-style: none;
  padding: 0;
}

.loading-message {
  display: flex;
  align-items: center;
}
.dots {
  display: inline-flex;
}
.dots span {
  animation: dots 1.5s infinite;
}
.dots span:nth-child(1) {
  animation-delay: 0s;
}
.dots span:nth-child(2) {
  animation-delay: 0.2s;
}
.dots span:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes dots {
  0%, 20% {
    opacity: 1;
  }
  40%, 100% {
    opacity: 0;
  }
}
</style>